import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Avatar, VStack, HStack, SimpleGrid, useToast, Badge, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter
} from '@chakra-ui/react';
import {
  MdFiberManualRecord, MdStop, MdPause, MdPlayArrow, MdFullscreen, MdFullscreenExit, MdPeople,
  MdMic, MdMicOff, MdVideocam, MdVideocamOff, MdScreenShare, MdStopScreenShare, MdCallEnd,
  MdLock, MdLockOpen
} from 'react-icons/md';
import { useTracks, useParticipants, useLocalParticipant, VideoTrack, useRoomContext } from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';
import axiosInstance from '../services/axiosInstance';
import CobraSpinner from '../shared/CobraSpinner';


// Individual Participant Video Card component
function ParticipantCard({ participant, cameraTrackRef, onMute, onKick, isInstructorOrAdmin }) {
  const isSpeaking = participant?.isSpeaking;
  const isMicEnabled = participant?.isMicrophoneEnabled !== false;
  const isCamEnabled = participant?.isCameraEnabled !== false && cameraTrackRef?.publication?.track && !cameraTrackRef?.publication?.isMuted;

  let role = "student";
  try {
    if (participant?.metadata) {
      const meta = JSON.parse(participant.metadata);
      if (meta.role) role = meta.role;
    }
  } catch (e) { }

  return (
    <Box
      position="relative"
      borderRadius="2xl"
      overflow="hidden"
      bg="#0f172a"
      border="2px solid"
      borderColor={isSpeaking ? "blue.400" : "rgba(255, 255, 255, 0.12)"}
      boxShadow={isSpeaking ? "0 0 24px rgba(59, 130, 246, 0.5)" : "0 10px 30px rgba(0, 0, 0, 0.4)"}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      h="100%"
      minH={{ base: "180px", md: "240px" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      _hover={{ transform: "translateY(-4px)", borderColor: "blue.300", boxShadow: "0 14px 35px rgba(0, 0, 0, 0.5)" }}
    >
      {/* Video Stream or Avatar Fallback */}
      {isCamEnabled && cameraTrackRef ? (
        <Box w="100%" h="100%" position="absolute" top={0} left={0} zIndex={1}>
          <VideoTrack trackRef={cameraTrackRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      ) : (
        <VStack spacing={3} zIndex={2} p={4} textAlign="center">
          <Box position="relative">
            <Avatar
              size="xl"
              name={participant.name || "طالب"}
              bg={role === 'instructor' || role === 'admin' ? "blue.600" : "purple.600"}
              color="white"
              boxShadow="2xl"
            />
            {isSpeaking && (
              <Box
                position="absolute"
                top="-8px" left="-8px" right="-8px" bottom="-8px"
                borderRadius="full"
                border="3px solid #3b82f6"
                animation="pulse 1.5s infinite"
                sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }}
              />
            )}
          </Box>
        </VStack>
      )}

      {/* Top Overlay Badges */}
      <Flex position="absolute" top={3} left={3} right={3} justify="space-between" align="center" zIndex={10} pointerEvents="auto">
        <HStack spacing={1.5} bg="rgba(15, 23, 42, 0.75)" px={2.5} py={1} borderRadius="xl" backdropFilter="blur(8px)" border="1px solid rgba(255,255,255,0.1)">
          {isMicEnabled ? (
            <MdMic color="#4ade80" size={16} />
          ) : (
            <MdMicOff color="#f87171" size={16} />
          )}
          <Badge fontSize="10px" colorScheme={role === 'instructor' || role === 'admin' ? 'purple' : 'blue'} variant="subtle" borderRadius="md" px={1.5}>
            {role === 'instructor' ? 'أستاذ' : role === 'admin' ? 'مشرف' : 'طالب'}
          </Badge>
        </HStack>

        {isInstructorOrAdmin && !participant.isLocal && role === 'student' && (
          <HStack spacing={1} bg="rgba(15, 23, 42, 0.75)" p={1} borderRadius="xl" backdropFilter="blur(8px)" border="1px solid rgba(255,255,255,0.1)">
            <Tooltip label="كتم الميكروفون" hasArrow>
              <IconButton
                icon={<MdMicOff />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                borderRadius="md"
                onClick={() => onMute(participant)}
                aria-label="Mute"
              />
            </Tooltip>
            <Tooltip label="طرد من الحصة" hasArrow>
              <IconButton
                icon={<MdStop />}
                size="xs"
                colorScheme="orange"
                variant="ghost"
                borderRadius="md"
                onClick={() => onKick(participant, false)}
                aria-label="Kick"
              />
            </Tooltip>
          </HStack>
        )}
      </Flex>

      {/* Bottom Participant Name Label */}
      <Box
        position="absolute"
        bottom={3}
        right={3}
        zIndex={10}
        bg="rgba(15, 23, 42, 0.85)"
        backdropFilter="blur(10px)"
        px={3}
        py={1}
        borderRadius="xl"
        border="1px solid rgba(255,255,255,0.1)"
        maxW="80%"
      >
        <Text color="white" fontSize="xs" fontWeight="bold" dir="rtl" isTruncated>
          {participant.name || "طالب"} {participant.isLocal ? "(أنت)" : ""}
        </Text>
      </Box>
    </Box>
  );
}

// Compact Participant List Item component (for Sidebar / List View)
function ParticipantListItem({ participant, cameraTrackRef, onMute, onKick, isInstructorOrAdmin }) {
  const isSpeaking = participant?.isSpeaking;
  const isMicEnabled = participant?.isMicrophoneEnabled !== false;
  const isCamEnabled = participant?.isCameraEnabled !== false && cameraTrackRef?.publication?.track && !cameraTrackRef?.publication?.isMuted;

  let role = "student";
  try {
    if (participant?.metadata) {
      const meta = JSON.parse(participant.metadata);
      if (meta.role) role = meta.role;
    }
  } catch (e) { }

  return (
    <Flex
      align="center"
      justify="space-between"
      p={3}
      borderRadius="xl"
      bg="rgba(15, 23, 42, 0.85)"
      border="1.5px solid"
      borderColor={isSpeaking ? "blue.400" : "rgba(255, 255, 255, 0.1)"}
      boxShadow={isSpeaking ? "0 0 16px rgba(59, 130, 246, 0.4)" : "0 4px 12px rgba(0, 0, 0, 0.3)"}
      backdropFilter="blur(10px)"
      transition="all 0.2s ease"
      dir="rtl"
      w="100%"
      _hover={{ bg: "rgba(30, 41, 59, 0.9)", borderColor: "blue.300" }}
    >
      <HStack spacing={3} overflow="hidden">
        {/* Video Thumbnail or Avatar */}
        <Box position="relative" w="42px" h="42px" borderRadius="lg" overflow="hidden" flexShrink={0} bg="gray.800">
          {isCamEnabled && cameraTrackRef ? (
            <VideoTrack trackRef={cameraTrackRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Avatar
              size="sm"
              name={participant.name || "طالب"}
              bg={role === 'instructor' || role === 'admin' ? "blue.600" : "purple.600"}
              color="white"
              w="100%" h="100%"
            />
          )}
          {isSpeaking && (
            <Box
              position="absolute"
              top="0" left="0" right="0" bottom="0"
              borderRadius="lg"
              border="2px solid #3b82f6"
              animation="pulse 1.5s infinite"
            />
          )}
        </Box>

        {/* Participant Name & Role */}
        <VStack align="start" spacing={0} overflow="hidden">
          <Text color="white" fontSize="sm" fontWeight="bold" isTruncated>
            {participant.name || "طالب"} {participant.isLocal ? "(أنت)" : ""}
          </Text>
          <Badge fontSize="9px" colorScheme={role === 'instructor' || role === 'admin' ? 'purple' : 'blue'} variant="subtle" borderRadius="md" px={1.5}>
            {role === 'instructor' ? 'أستاذ' : role === 'admin' ? 'مشرف' : 'طالب'}
          </Badge>
        </VStack>
      </HStack>

      {/* Mic Status & Actions */}
      <HStack spacing={2} flexShrink={0}>
        <Box p={1.5} borderRadius="lg" bg="rgba(0,0,0,0.4)">
          {isMicEnabled ? (
            <MdMic color="#4ade80" size={18} />
          ) : (
            <MdMicOff color="#f87171" size={18} />
          )}
        </Box>

        {isInstructorOrAdmin && !participant.isLocal && role === 'student' && (
          <HStack spacing={1}>
            <Tooltip label="كتم الميكروفون" hasArrow>
              <IconButton
                icon={<MdMicOff />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                borderRadius="md"
                onClick={() => onMute(participant)}
                aria-label="Mute"
              />
            </Tooltip>
            <Tooltip label="طرد" hasArrow>
              <IconButton
                icon={<MdStop />}
                size="xs"
                colorScheme="orange"
                variant="ghost"
                borderRadius="md"
                onClick={() => onKick(participant, false)}
                aria-label="Kick"
              />
            </Tooltip>
          </HStack>
        )}
      </HStack>
    </Flex>
  );
}

// Web Audio API chime tone disabled as requested
const playChimeSound = () => {
  // Audio chime disabled
};

export default function CustomMeetingLayout({ socket, roomName, secretKey, user }) {
  const toast = useToast();
  const [showRaisedHandsModal, setShowRaisedHandsModal] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isLocalRecording, setIsLocalRecording] = useState(false);
  const [isLocalRecordingPaused, setIsLocalRecordingPaused] = useState(false);
  const [isScreenShareMaximized, setIsScreenShareMaximized] = useState(false);
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const formatDuration = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // States for Mute-All Lock and Raise-Hand
  const [isMuteAllLocked, setIsMuteAllLocked] = useState(false);
  const [raisedHands, setRaisedHands] = useState([]);
  const [hasRaisedHand, setHasRaisedHand] = useState(false);
  const [isIndividualMicGranted, setIsIndividualMicGranted] = useState(false);

  const mediaRecorderRef = useRef(null);
  const screenStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);
  const recordingDurationRef = useRef(0);
  const recordingTimerRef = useRef(null);
  const recordingSessionIdRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const chunkQueueRef = useRef([]);
  const isUploadingChunkRef = useRef(false);
  // Refs to always hold the latest values (prevents stale closures in socket/LiveKit event handlers)
  const isInstructorOrAdminRef = useRef(false);
  const isMuteAllLockedRef = useRef(false);
  const isIndividualMicGrantedRef = useRef(false);

  // Prevent accidental tab close while recording
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isLocalRecording) {
        e.preventDefault();
        e.returnValue = "يوجد تسجيل حصة جاري حالياً. هل أنت متأكد من المغادرة؟";
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isLocalRecording]);


  // LiveKit Track Hooks
  const cameraTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  const screenTracks = useTracks([{ source: Track.Source.ScreenShare }], { onlySubscribed: false });
  const hasScreenShare = screenTracks && screenTracks.length > 0;

  // Local Participant controls
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
  const room = useRoomContext();

  const participants = useParticipants();
  const participantCount = participants.length;

  let localMetaRole = user?.role?.toLowerCase() || 'student';
  try {
    if (localParticipant?.metadata) {
      const meta = JSON.parse(localParticipant.metadata);
      if (meta.role) localMetaRole = meta.role.toLowerCase();
    }
  } catch (e) { }

  const isInstructorOrAdmin =
    localMetaRole === 'admin' ||
    localMetaRole === 'instructor' ||
    localMetaRole === 'teacher' ||
    user?.role?.toLowerCase() === 'admin' ||
    user?.role?.toLowerCase() === 'instructor' ||
    user?.role?.toLowerCase() === 'teacher';

  // Keep refs in sync so event handlers always read the current values
  isInstructorOrAdminRef.current = isInstructorOrAdmin;
  isMuteAllLockedRef.current = isMuteAllLocked;
  isIndividualMicGrantedRef.current = isIndividualMicGranted;

  // Auto-mute microphone for instructors/admins when they first join the room
  // This lets them observe the session and work in other tabs without producing noise
  const didAutoMuteRef = useRef(false);
  useEffect(() => {
    if (!localParticipant || didAutoMuteRef.current) return;
    if (!isInstructorOrAdmin) return;
    didAutoMuteRef.current = true;
    localParticipant.setMicrophoneEnabled(false).catch((err) => {
      console.warn('[AutoMute] Could not mute instructor mic on join:', err);
    });
  }, [localParticipant, isInstructorOrAdmin]);

  const sendLiveKitData = useCallback((dataObj) => {
    try {
      if (!room || !room.localParticipant) return;
      const str = JSON.stringify(dataObj);
      const encoder = new TextEncoder();
      const payload = encoder.encode(str);
      room.localParticipant.publishData(payload, { reliable: true });
    } catch (err) {
      console.warn("sendLiveKitData error:", err);
    }
  }, [room]);

  const handleToggleMic = async () => {
    if (!localParticipant) return;

    // If student and mic is locked by instructor without individual permission
    if (!isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted) {
      toast({
        title: "الميكروفون مقفل حالياً 🔒",
        description: "قام الأستاذ بقفل الميكروفونات للجميع. يمكنك الضغط على زر (رفع اليد ✋) لطلب الكلمة.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top-right"
      });
      if (isMicrophoneEnabled) {
        try {
          await localParticipant.setMicrophoneEnabled(false);
        } catch (_) {}
      }
      return;
    }

    await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
  };

  // Continuous enforcement: ensure student cannot open mic by any means when locked
  useEffect(() => {
    if (isInstructorOrAdmin || !localParticipant) return;
    if (isMuteAllLocked && !isIndividualMicGranted && isMicrophoneEnabled) {
      localParticipant.setMicrophoneEnabled(false).catch((err) => {
        console.warn("Enforce mute lock failed:", err);
      });
    }
  }, [isMuteAllLocked, isIndividualMicGranted, isMicrophoneEnabled, localParticipant, isInstructorOrAdmin]);

  const handleToggleCamera = async () => {
    if (localParticipant) {
      await localParticipant.setCameraEnabled(!isCameraEnabled);
    }
  };

  const handleToggleScreenShare = async () => {
    if (!localParticipant) return;

    // Check browser support for screen sharing (getDisplayMedia)
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
      toast({
        title: "مشاركة الشاشة غير مدعومة",
        description: "متصفح الهاتف الحالي (مثل Safari على iPhone) لا يدعم مشاركة الشاشة من المتصفح. يرجى استخدام متصفح الكمبيوتر أو تطبيق متوافق.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }

    try {
      if (isScreenShareEnabled) {
        await localParticipant.setScreenShareEnabled(false);
      } else {
        // Try with audio: false to prevent Android mobile browser rejection
        try {
          await localParticipant.setScreenShareEnabled(true, { audio: false });
        } catch (firstErr) {
          console.warn("First screen share attempt failed, trying default fallback:", firstErr);
          await localParticipant.setScreenShareEnabled(true);
        }
      }
    } catch (err) {
      console.error("Error toggling screen share:", err);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      let errorMsg = "تم رفض أو إلغاء مشاركة الشاشة.";

      if (err.name === "NotSupportedError" || err.message?.includes("NotSupported")) {
        errorMsg = isMobile
          ? "مشاركة الشاشة غير مدعومة على متصفح الهاتف هذا. يرجى استخدام جهاز كمبيوتر أو متصفح يدعم الميزة."
          : "مشاركة الشاشة غير مدعومة في هذا المتصفح.";
      } else if (err.name === "NotAllowedError" || err.message?.includes("Permission denied")) {
        errorMsg = "تم إيقاف أو رفض إذن مشاركة الشاشة.";
      }

      toast({
        title: "تعذر مشاركة الشاشة",
        description: errorMsg,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right"
      });
    }
  };

  const handleFullscreenShare = () => {
    setIsScreenShareMaximized(prev => !prev);
  };

  useEffect(() => {
    if (!socket) return;

    // Ensure socket joined roomName signaling room
    const joinSignalingRoom = () => {
      socket.emit("join_meeting_room", { roomName, user });
    };

    if (socket.connected) {
      joinSignalingRoom();
    }
    socket.on("connect", joinSignalingRoom);

    socket.on("admission_request_received", (data) => {
      setPendingRequests(prev => [...prev, data]);
      toast({ title: "طلب انضمام جديد", status: "info", duration: 3000, position: "top-right", isClosable: true });
    });

    socket.on("admission_handled", (data) => {
      setPendingRequests(prev => prev.filter(req => req.socketId !== data.targetSocketId));
    });

    socket.on("room_state_update", (state) => {
      if (state.isRecording !== undefined) setIsRecording(state.isRecording);
      if (state.isMuteAllLocked !== undefined) setIsMuteAllLocked(state.isMuteAllLocked);
      if (state.raisedHands) {
        setRaisedHands(state.raisedHands);
        const myId = (user?._id || user?.id || '').toString();
        const myIdentity = (localParticipant?.identity || '').toString();
        const amIRaised = state.raisedHands.some(h =>
          (myId && (h.userId || '').toString() === myId) ||
          (myIdentity && (h.identity || '').toString() === myIdentity) ||
          (myIdentity && (h.userId || '').toString() === myIdentity) ||
          (socket.id && (h.socketId || '') === socket.id)
        );
        setHasRaisedHand(amIRaised);
      }
    });

    socket.on("recording_state_changed", (state) => {
      setIsRecording(state.isRecording);
      toast({
        title: state.isRecording ? "بدأ التسجيل" : "توقف التسجيل",
        status: state.isRecording ? "success" : "warning",
        duration: 2000,
        position: "top-right"
      });
    });

    // Mute All Lock state changed
    socket.on("mute_all_locked_changed", async (data) => {
      setIsMuteAllLocked(data.locked);
      if (data.locked) {
        if (!isInstructorOrAdmin) {
          setIsIndividualMicGranted(false);
          if (localParticipant) {
            try {
              await localParticipant.setMicrophoneEnabled(false);
            } catch (e) {
              console.warn("Could not mute local participant:", e);
            }
          }
          toast({
            title: "تم قفل الميكروفونات للجميع 🔒",
            description: `قام ${data.byName || 'الأستاذ'} بقفل الميكروفونات للجميع. يمكنك الضغط على زر (رفع اليد ✋) لطلب الكلمة.`,
            status: "warning",
            duration: 5000,
            isClosable: true,
            position: "top-right"
          });
        }
      } else {
        if (!isInstructorOrAdmin) {
          setIsIndividualMicGranted(false);
          toast({
            title: "تم فتح الميكروفونات للجميع 🔓",
            description: "يمكنك الآن تشغيل الميكروفون عند رغبتك في المشاركة.",
            status: "success",
            duration: 4000,
            isClosable: true,
            position: "top-right"
          });
        }
      }
    });

    // Raised hands updated list
    socket.on("raised_hands_updated", (data) => {
      if (data.raisedHands) {
        setRaisedHands(data.raisedHands);
        const myId = (user?._id || user?.id || '').toString();
        const myIdentity = (localParticipant?.identity || '').toString();
        const amIRaised = data.raisedHands.some(h =>
          (myId && (h.userId || '').toString() === myId) ||
          (myIdentity && (h.identity || '').toString() === myIdentity) ||
          (myIdentity && (h.userId || '').toString() === myIdentity) ||
          (socket.id && (h.socketId || '') === socket.id)
        );
        setHasRaisedHand(amIRaised);
      }
    });

    // Student raised hand alert (for instructors/admins)
    socket.on("student_raised_hand", (data) => {
      if (isInstructorOrAdminRef.current) {
        setShowRaisedHandsModal(true); // Automatically open the pop-up modal for instructor!
        toast({
          title: "✋ طالب يرغب في التحدث",
          description: `قام الطالب "${data.student?.name || 'طالب'}" برفع يده لطلب الكلمة.`,
          status: "info",
          duration: 6000,
          isClosable: true,
          position: "top-right"
        });
      }
    });

    // Mic permission granted to student
    socket.on("mic_permission_granted", async (data) => {
      const myId = (user?._id || user?.id || '').toString();
      const myIdentity = (localParticipant?.identity || '').toString();
      const isTarget =
        (myId && data.targetUserId?.toString() === myId) ||
        (myIdentity && data.targetIdentity?.toString() === myIdentity) ||
        (myIdentity && data.targetUserId?.toString() === myIdentity) ||
        (myId && data.targetIdentity?.toString() === myId);
      if (isTarget) {
        setIsIndividualMicGranted(true);
        setHasRaisedHand(false);
        try {
          if (localParticipant) {
            await localParticipant.setMicrophoneEnabled(true);
          }
        } catch (err) {
          console.warn("Could not auto enable mic:", err);
        }
        toast({
          title: "تم فتح الميكروفون لك! 🎙️",
          description: "سمح لك الأستاذ بالتحدث الآن. الميكروفون مفتوح ويمكنك التكلم مباشرة.",
          status: "success",
          duration: 7000,
          isClosable: true,
          position: "top-right"
        });
      }
    });

    // Mic muted by instructor
    socket.on("mic_muted_by_instructor", async (data) => {
      const myId = (user?._id || user?.id || '').toString();
      const myIdentity = (localParticipant?.identity || '').toString();
      const isTarget =
        (myId && data.targetUserId?.toString() === myId) ||
        (myIdentity && data.targetIdentity?.toString() === myIdentity) ||
        (myIdentity && data.targetUserId?.toString() === myIdentity) ||
        (myId && data.targetIdentity?.toString() === myId);
      if (isTarget) {
        setIsIndividualMicGranted(false);
        try {
          if (localParticipant) {
            await localParticipant.setMicrophoneEnabled(false);
          }
        } catch (err) {
          console.warn("Could not disable mic:", err);
        }
        toast({
          title: "تم قفل الميكروفون 🔇",
          description: "قام الأستاذ بقفل الميكروفون الخاص بك.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "top-right"
        });
      }
    });

    return () => {
      socket.off("connect", joinSignalingRoom);
      socket.off("admission_request_received");
      socket.off("admission_handled");
      socket.off("room_state_update");
      socket.off("recording_state_changed");
      socket.off("mute_all_locked_changed");
      socket.off("raised_hands_updated");
      socket.off("student_raised_hand");
      socket.off("mic_permission_granted");
      socket.off("mic_muted_by_instructor");
    };
  }, [socket, toast, isInstructorOrAdmin, localParticipant, user, roomName]);

  // LiveKit: enforce mute lock when student tries to publish/unmute their mic track
  useEffect(() => {
    if (!room || !localParticipant) return;

    const handleLocalTrackPublished = (publication) => {
      // If a student publishes a microphone track while mute lock is active, force-mute immediately
      if (
        !isInstructorOrAdminRef.current &&
        isMuteAllLockedRef.current &&
        !isIndividualMicGrantedRef.current &&
        publication?.source === Track.Source.Microphone
      ) {
        localParticipant.setMicrophoneEnabled(false).catch((err) => {
          console.warn('[MuteLock] Forced mute on track publish:', err);
        });
      }
    };

    room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    return () => {
      room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    };
  }, [room, localParticipant]);

  // LiveKit WebRTC DataChannel (Instant peer-to-peer sync without relying on external socket servers)
  useEffect(() => {
    if (!room) return;

    const handleDataReceived = (payload) => {
      try {
        const decoder = new TextDecoder();
        const str = decoder.decode(payload);
        const msg = JSON.parse(str);

        if (msg.type === 'RAISE_HAND') {
          const student = msg.student;
          setRaisedHands(prev => {
            const exists = prev.some(h => (h.userId && h.userId === student.userId) || (h.identity && h.identity === student.identity));
            if (exists) return prev;
            return [...prev, student];
          });

          if (isInstructorOrAdminRef.current) {
            setShowRaisedHandsModal(true); // Automatically show pop-up on instructor screen!
            toast({
              title: "✋ طالب يرغب في التحدث",
              description: `قام الطالب "${student.name || 'طالب'}" برفع يده لطلب الكلمة.`,
              status: "info",
              duration: 6000,
              isClosable: true,
              position: "top-right"
            });
          }
        } else if (msg.type === 'LOWER_HAND') {
          setRaisedHands(prev => prev.filter(h => h.userId !== msg.userId && h.identity !== msg.identity));
          // Reset raised hand state if this message targets the current user
          const myId = (user?._id || user?.id || '').toString();
          if (msg.userId?.toString() === myId || msg.identity === localParticipant?.identity) {
            setHasRaisedHand(false);
          }
        } else if (msg.type === 'ALLOW_MIC') {
          const myId = (user?._id || user?.id || '').toString();
          const isTarget = msg.targetUserId?.toString() === myId || msg.targetIdentity === localParticipant?.identity;
          if (isTarget) {
            setIsIndividualMicGranted(true);
            setHasRaisedHand(false);
            try {
              if (localParticipant) {
                localParticipant.setMicrophoneEnabled(true);
              }
            } catch (err) {
              console.warn("Could not auto enable mic:", err);
            }
            toast({
              title: "تم فتح الميكروفون لك! 🎙️",
              description: "سمح لك الأستاذ بالتحدث الآن. الميكروفون يعمل مباشرة.",
              status: "success",
              duration: 7000,
              isClosable: true,
              position: "top-right"
            });
          }
          setRaisedHands(prev => prev.filter(h => h.userId !== msg.targetUserId && h.identity !== msg.targetIdentity));
        } else if (msg.type === 'MUTE_MIC') {
          const myId = (user?._id || user?.id || '').toString();
          const isTarget = msg.targetUserId?.toString() === myId || msg.targetIdentity === localParticipant?.identity;
          if (isTarget) {
            setIsIndividualMicGranted(false);
            try {
              if (localParticipant) {
                localParticipant.setMicrophoneEnabled(false);
              }
            } catch (err) {
              console.warn("Could not disable mic:", err);
            }
            toast({
              title: "تم قفل الميكروفون 🔇",
              description: "قام الأستاذ بقفل الميكروفون الخاص بك.",
              status: "warning",
              duration: 4000,
              isClosable: true,
              position: "top-right"
            });
          }
        } else if (msg.type === 'TOGGLE_MUTE_LOCK') {
          setIsMuteAllLocked(msg.locked);
          if (msg.locked) {
            if (!isInstructorOrAdminRef.current) {
              setIsIndividualMicGranted(false);
              if (localParticipant) {
                localParticipant.setMicrophoneEnabled(false);
              }
              toast({
                title: "تم قفل الميكروفونات للجميع 🔒",
                description: `قام ${msg.byName || 'الأستاذ'} بقفل الميكروفونات للجميع. يمكنك الضغط على زر (رفع اليد ✋) لطلب الكلمة.`,
                status: "warning",
                duration: 5000,
                isClosable: true,
                position: "top-right"
              });
            }
          } else {
            if (!isInstructorOrAdminRef.current) {
              setIsIndividualMicGranted(false);
              toast({
                title: "تم فتح الميكروفونات للجميع 🔓",
                description: "يمكنك الآن تشغيل الميكروفون عند رغبتك في المشاركة.",
                status: "success",
                duration: 4000,
                isClosable: true,
                position: "top-right"
              });
            }
          }
        }
      } catch (err) {
        console.warn("Error parsing LiveKit data message:", err);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);
    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room, isInstructorOrAdmin, localParticipant, user, toast]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const handleKick = async (participant, block = false) => {
    try {
      const response = await axiosInstance.post('/meetings/kick', {
        roomName,
        identity: participant.identity,
        block
      });
      if (response.data.success) {
        toast({ title: response.data.message || "تم العملية بنجاح", status: "success", duration: 2000, position: "top-right" });
      }
    } catch (err) {
      toast({ title: err.response?.data?.error || "حدث خطأ", status: "error", duration: 3000, position: "top-right" });
    }
  };

  const handleMuteMic = async (participant) => {
    try {
      const response = await axiosInstance.post('/meetings/mute', {
        roomName,
        identity: participant.identity,
        muted: true
      });
      if (response.data.success) {
        toast({
          title: response.data.message || "تم كتم الميكروفون بنجاح",
          status: "success",
          duration: 2000,
          position: "top-right"
        });
      }
    } catch (err) {
      toast({
        title: err.response?.data?.error || "حدث خطأ في كتم الصوت",
        status: "error",
        duration: 3000,
        position: "top-right"
      });
    }
  };

  const handleMuteAllStudents = async (showNotification = true) => {
    const studentParticipants = participants.filter(p => {
      if (p.isLocal) return false;
      let role = "student";
      try {
        if (p.metadata) {
          const meta = JSON.parse(p.metadata);
          if (meta.role) role = meta.role;
        }
      } catch (e) { }
      return role === 'student';
    });

    if (studentParticipants.length === 0) {
      if (showNotification) {
        toast({ title: "لا يوجد طلاب في الحصة حالياً لكتم صوتهم", status: "info", duration: 2000, position: "top-right" });
      }
      return;
    }

    try {
      await Promise.all(
        studentParticipants.map(p =>
          axiosInstance.post('/meetings/mute', { roomName, identity: p.identity, muted: true })
        )
      );
      if (showNotification) {
        toast({ title: "تم كتم ميكروفونات جميع الطلاب بنجاح", status: "success", duration: 3000, position: "top-right" });
      }
    } catch (err) {
      if (showNotification) {
        toast({ title: "حدث خطأ أثناء كتم صوت بعض الطلاب", status: "error", duration: 3000, position: "top-right" });
      }
    }
  };

  // Toggle Mute All Lock (Instructor)
  const handleToggleMuteAllLock = async () => {
    const newLocked = !isMuteAllLocked;
    setIsMuteAllLocked(newLocked);
    const byName = user?.username || user?.name || "الأستاذ";

    if (socket) {
      socket.emit("toggle_mute_all_lock", {
        roomName,
        locked: newLocked,
        byName
      });
    }

    sendLiveKitData({
      type: 'TOGGLE_MUTE_LOCK',
      locked: newLocked,
      byName
    });

    if (newLocked) {
      await handleMuteAllStudents(false);
      toast({
        title: "تم كتم وقفل الميكروفونات للجميع 🔒",
        description: "لا يمكن للطلاب تشغيل المايك إلا بعد فتحه من طرفك أو قبول طلب رفع اليد.",
        status: "success",
        duration: 4000,
        position: "top-right"
      });
    } else {
      toast({
        title: "تم فتح إمكانية التحدث للجميع 🔓",
        description: "يمكن لجميع الطلاب الآن فتح الميكروفون والمشاركة بحرية.",
        status: "success",
        duration: 4000,
        position: "top-right"
      });
    }
  };

  // Toggle Raise Hand (Student)
  const handleToggleRaiseHand = () => {
    const myId = (user?._id || user?.id || localParticipant?.identity || socket?.id || '').toString();
    const myName = user?.username || user?.name || localParticipant?.name || "طالب";
    const myIdentity = (localParticipant?.identity || myId).toString();

    if (hasRaisedHand) {
      if (socket) {
        socket.emit("lower_hand", { roomName, userId: myId });
        if (myIdentity && myIdentity !== myId) {
          socket.emit("lower_hand", { roomName, userId: myIdentity });
        }
      }
      sendLiveKitData({
        type: 'LOWER_HAND',
        userId: myId,
        identity: myIdentity
      });
      setHasRaisedHand(false);
      toast({
        title: "تم خفض اليد",
        status: "info",
        duration: 2000,
        position: "top-right"
      });
    } else {
      const studentData = {
        userId: myId,
        id: myId,
        _id: myId,
        name: myName,
        username: myName,
        identity: myIdentity,
        timestamp: Date.now()
      };

      if (socket) {
        socket.emit("raise_hand", {
          roomName,
          user: studentData
        });
      }

      sendLiveKitData({
        type: 'RAISE_HAND',
        student: studentData
      });

      setHasRaisedHand(true);
      toast({
        title: "تم رفع اليد ✋",
        description: "تم إشعار الأستاذ برغبتك في التحدث. يرجى الانتظار حتى يمنحك الكلمة.",
        status: "info",
        duration: 4000,
        position: "top-right"
      });
    }
  };

  // Allow Student to Speak (Instructor accepts raised hand)
  const handleAllowStudentMic = async (hand) => {
    // Find participant matching identity or name
    const matchingParticipant = participants.find(p => {
      const pId = (p.identity || '').toString();
      const targetId = (hand.userId || '').toString();
      return pId === targetId || p.name === hand.name;
    });

    const targetIdentity = matchingParticipant ? matchingParticipant.identity : (hand.identity || hand.userId);

    if (socket) {
      socket.emit("allow_student_mic", {
        roomName,
        targetUserId: hand.userId,
        targetIdentity,
        studentName: hand.name
      });
    }

    sendLiveKitData({
      type: 'ALLOW_MIC',
      targetUserId: hand.userId,
      targetIdentity,
      studentName: hand.name
    });

    setRaisedHands(prev => prev.filter(h => h.userId !== hand.userId && h.identity !== targetIdentity));

    // Also un-mute via server if needed
    if (matchingParticipant) {
      axiosInstance.post('/meetings/mute', {
        roomName,
        identity: matchingParticipant.identity,
        muted: false
      }).catch(() => { });
    }

    toast({
      title: `تم فتح المايك للطالب "${hand.name}" 🎙️`,
      description: "يمكن للطالب الآن التحدث مباشرة.",
      status: "success",
      duration: 3500,
      position: "top-right"
    });
  };

  // Dismiss / Lower student's hand (Instructor)
  const handleDismissHand = (hand) => {
    if (socket) socket.emit("lower_hand", { roomName, userId: hand.userId });
    sendLiveKitData({
      type: 'LOWER_HAND',
      userId: hand.userId,
      identity: hand.identity
    });
    setRaisedHands(prev => prev.filter(h => h.userId !== hand.userId));
  };

  const handleEndMeetingForEveryone = async () => {
    if (window.confirm("هل أنت تأكد من إغلاق وإنهاء الحصة للجميع؟")) {
      try {
        await axiosInstance.post(`/meetings/end/${encodeURIComponent(roomName)}`);
        toast({
          title: "تم إنهاء الحصة للجميع بنجاح",
          status: "success",
          duration: 3000,
          position: "top-right"
        });
        window.history.back();
      } catch (err) {
        toast({
          title: "خطأ في إنهاء الحصة",
          description: err.response?.data?.error || err.message,
          status: "error",
          duration: 3000,
          position: "top-right"
        });
      }
    }
  };

  const handleAdmit = (req, approved) => {
    socket.emit("handle_admission", { roomName, targetSocketId: req.socketId, approved, secretKey });
    setPendingRequests(prev => prev.filter(r => r.socketId !== req.socketId));
  };

  const startRecording = async () => {
    let screenStream = null;
    let micStream = null;
    let audioContext = null;

    try {
      if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
        throw new Error("NotSupportedError");
      }
      try {
        screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: "monitor"
          },
          audio: {
            suppressLocalAudioPlayback: false,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          },
          systemAudio: "include",
          selfBrowserSurface: "include"
        });
      } catch (firstErr) {
        try {
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true
          });
        } catch (audioErr) {
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true
          });
        }
      }
    } catch (err) {
      console.error("Error capturing screen:", err);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      toast({
        title: "تعذر بدء تسجيل الشاشة",
        description: isMobile
          ? "مشاركة وتسجيل الشاشة غير مدعوم في متصفح الهاتف الحالي. يرجى استخدام متصفح الكمبيوتر."
          : "يرجى منح الإذن لمشاركة الشاشة لتسجيل الحصة.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }

    try {
      const audioTracks = [];
      const screenAudioTracks = screenStream.getAudioTracks();

      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
      } catch (micErr) {
        console.warn("No mic access or mic not available:", micErr);
      }

      const micAudioTracks = micStream ? micStream.getAudioTracks() : [];

      if (screenAudioTracks.length > 0 || micAudioTracks.length > 0) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const destination = audioContext.createMediaStreamDestination();

        // Connect Desktop / Bureau System Audio
        if (screenAudioTracks.length > 0) {
          const screenSource = audioContext.createMediaStreamSource(new MediaStream([screenAudioTracks[0]]));
          const screenGain = audioContext.createGain();
          screenGain.gain.value = 1.0;
          screenSource.connect(screenGain);
          screenGain.connect(destination);
        }

        // Connect Microphone Audio
        if (micAudioTracks.length > 0) {
          const micSource = audioContext.createMediaStreamSource(new MediaStream([micAudioTracks[0]]));
          const micGain = audioContext.createGain();
          micGain.gain.value = 1.0;
          micSource.connect(micGain);
          micGain.connect(destination);
        }

        audioTracks.push(destination.stream.getAudioTracks()[0]);
      }

      // Notify user if screen system audio was not checked in Chrome browser picker
      if (screenAudioTracks.length === 0) {
        toast({
          title: "تنبيه حول صوت النظام (Bureau)",
          description: "لتسجيل صوت التطبيقات والنظام، حدد 'الشاشة بأكملها (Entire Screen)' وتأكد من تفعيل خيار 'مشاركة صوت النظام (Share system audio)' عند اختيار الشاشة.",
          status: "info",
          duration: 6000,
          isClosable: true,
          position: "top-right"
        });
      }

      const videoTrack = screenStream.getVideoTracks()[0];
      const combinedStream = new MediaStream([videoTrack, ...audioTracks]);

      screenStreamRef.current = screenStream;
      micStreamRef.current = micStream;
      audioContextRef.current = audioContext;

      chunksRef.current = [];
      chunkIndexRef.current = 0;
      const sessionId = `rec_${(roomName || 'meeting').replace(/[^a-zA-Z0-9_-]/g, '_')}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      recordingSessionIdRef.current = sessionId;

      // Prioritize modern MP4 if browser supports it, otherwise high-quality WebM
      const preferredMimeTypes = [
        'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4',
        'video/webm;codecs=h264,opus',
        'video/webm;codecs=h264',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm'
      ];
      let options = {};
      for (const mime of preferredMimeTypes) {
        if (MediaRecorder.isTypeSupported(mime)) {
          options = { mimeType: mime };
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsUploadingRecording(true);

        try {
          if (chunksRef.current.length > 0) {
            const mimeType = mediaRecorder.mimeType || 'video/webm';
            const blob = new Blob(chunksRef.current, { type: mimeType });
            const isMp4 = mimeType.toLowerCase().includes('mp4');
            const ext = isMp4 ? 'mp4' : 'webm';
            const safeName = (roomName || 'meeting').replace(/[/\\?%*:|"<>]/g, '_');
            const now = new Date();
            const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
            const filename = `recording_${safeName}_${dateStr}.${ext}`;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }, 1000);

            const sizeMb = (blob.size / (1024 * 1024)).toFixed(1);
            toast({
              title: "تم حفظ وتنزيل تسجيل الحصة محلياً! 💾✅",
              description: `تم حفظ الملف (${sizeMb} MB) في مجلد التنزيلات باسم: ${filename}`,
              status: "success",
              duration: 8000,
              isClosable: true,
              position: "top-right"
            });
          }
        } catch (saveErr) {
          console.error("Local recording save error:", saveErr);
          toast({
            title: "خطأ أثناء حفظ التسجيل",
            description: saveErr.message,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right"
          });
        } finally {
          setIsUploadingRecording(false);
          chunksRef.current = [];
        }
      };

      videoTrack.onended = () => {
        stopRecording();
      };

      recordingStartTimeRef.current = Date.now();
      recordingDurationRef.current = 0;
      setRecordingDuration(0);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => {
        recordingDurationRef.current += 1;
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      // Start recording with 1-second timeslice to continuously collect chunks safely
      mediaRecorder.start(1000);
      setIsLocalRecording(true);

      if (socket) {
        socket.emit("toggle_recording", { roomName, isRecording: true });
      }

      toast({
        title: "بدأ تسجيل الحصة محلياً 🔴",
        description: "يتم تسجيل الشاشة والصوت بجودة عالية. عند إيقاف التسجيل سيتم تنزيل وحفظ ملف الفيديو مباشرة على جهازك.",
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });

    } catch (recorderErr) {
      console.error("Error setting up recorder:", recorderErr);
      toast({
        title: "فشل بدء التسجيل",
        description: recorderErr.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      if (screenStream) screenStream.getTracks().forEach(t => t.stop());
      if (micStream) micStream.getTracks().forEach(t => t.stop());
    }
  };

  const stopRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsLocalRecording(false);
    setIsLocalRecordingPaused(false);
    setRecordingDuration(0);

    if (socket) {
      socket.emit("toggle_recording", { roomName, isRecording: false });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsLocalRecordingPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsLocalRecordingPaused(false);
    }
  };

  const handlePauseResumeRecording = () => {
    if (isLocalRecordingPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  const handleToggleRecording = () => {
    if (isLocalRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Determine participant column grid size
  const gridColumns = {
    base: 1,
    sm: participantCount > 1 ? 2 : 1,
    md: participantCount > 4 ? 3 : participantCount > 1 ? 2 : 1,
    lg: participantCount > 6 ? 4 : participantCount > 2 ? 3 : participantCount > 1 ? 2 : 1
  };

  return (
    <Box
      className="my-custom-meeting-layout"
      position="absolute"
      top={0} left={0} right={0} bottom={0}
      bg="#090d16"
      display="flex"
      flexDirection="column"
      fontFamily="Tajawal, sans-serif"
      zIndex={50}
      overflow="hidden"
    >
      {/* 1. TOP HEADER BAR */}
      <Flex
        w="100%"
        h="60px"
        bg="rgba(15, 23, 42, 0.9)"
        px={6}
        align="center"
        justify="space-between"
        borderBottom="1px solid rgba(255,255,255,0.1)"
        backdropFilter="blur(12px)"
        zIndex={100}
      >
        <HStack spacing={3}>
          <Badge colorScheme="blue" variant="solid" px={3} py={1} borderRadius="lg" fontSize="xs" fontWeight="bold">
            {roomName}
          </Badge>
          {isRecording && (
            <HStack spacing={1.5} bg="red.500" color="white" px={2.5} py={0.5} borderRadius="full">
              <Box w={2} h={2} bg="white" borderRadius="full" className="pulse-animation" />
              <Text fontSize="11px" fontWeight="bold">التسجيل جارٍ (محلي)</Text>
            </HStack>
          )}
          {isUploadingRecording && (
            <HStack spacing={2} bg="blue.600" color="white" px={3} py={0.5} borderRadius="full" boxShadow="0 0 12px rgba(59,130,246,0.6)">
              <CobraSpinner size="xs" color="white" />
              <Text fontSize="11px" fontWeight="bold">جاري حفظ وتنزيل الفيديو...</Text>
            </HStack>
          )}
        </HStack>

        <HStack spacing={3}>
          {/* Raised Hands alert button in header for Teacher */}
          {isInstructorOrAdmin && raisedHands.length > 0 && (
            <Button
              size="xs"
              colorScheme="yellow"
              variant="solid"
              borderRadius="xl"
              px={3}
              py={1}
              onClick={() => setShowRaisedHandsModal(true)}
              animation="pulse 1.8s infinite"
              sx={{ '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.06)' } } }}
            >
              ✋ طلبات الكلمة ({raisedHands.length})
            </Button>
          )}



          {isInstructorOrAdmin && (
            <HStack spacing={2} bg="rgba(255,255,255,0.05)" px={3} py={1} borderRadius="xl">
              <IconButton
                icon={isLocalRecording ? <MdStop /> : <MdFiberManualRecord />}
                size="xs"
                colorScheme={isLocalRecording ? "red" : "gray"}
                variant="solid"
                onClick={handleToggleRecording}
                aria-label="Toggle Recording"
                isDisabled={isUploadingRecording}
                isRound
              />
              {isLocalRecording && (
                <IconButton
                  icon={isLocalRecordingPaused ? <MdPlayArrow /> : <MdPause />}
                  size="xs"
                  colorScheme="gray"
                  variant="ghost"
                  color="white"
                  onClick={handlePauseResumeRecording}
                  aria-label="Pause Recording"
                  isRound
                />
              )}
              <Text fontSize="xs" color="gray.300" fontWeight="medium">
                {isUploadingRecording
                  ? "جاري الحفظ..."
                  : isLocalRecording
                    ? (isLocalRecordingPaused
                        ? `مؤقت (${formatDuration(recordingDuration)})`
                        : `تسجيل محلي (${formatDuration(recordingDuration)})`)
                    : "تسجيل محلي"}
              </Text>
            </HStack>
          )}
        </HStack>
      </Flex>


      {/* 2. MAIN CONTENT AREA (CARDS GRID & SCREEN SHARE FOCUS CARD) */}
      <Flex
        flex={1}
        w="100%"
        h="100%"
        overflow="hidden"
        position="relative"
        p={isScreenShareMaximized ? 0 : (hasScreenShare ? 2 : 4)}
        pb={{ base: isScreenShareMaximized ? 0 : "80px", md: isScreenShareMaximized ? 0 : "90px" }}
        gap={4}
        direction="column"
      >

        {/* Screen Share Card (Takes FULL area when screen is shared - No side attendees list to block view) */}
        {hasScreenShare && (
          <Box
            flex={1}
            w="100%"
            h="100%"
            position={isScreenShareMaximized ? "absolute" : "relative"}
            top={isScreenShareMaximized ? 0 : undefined}
            left={isScreenShareMaximized ? 0 : undefined}
            right={isScreenShareMaximized ? 0 : undefined}
            bottom={isScreenShareMaximized ? 0 : undefined}
            zIndex={isScreenShareMaximized ? 30 : 1}
            borderRadius={isScreenShareMaximized ? 0 : "2xl"}
            overflow="hidden"
            bg="#0f172a"
            border={isScreenShareMaximized ? "none" : "2px solid rgba(59, 130, 246, 0.4)"}
            boxShadow={isScreenShareMaximized ? "none" : "0 10px 30px rgba(0,0,0,0.5)"}
          >
            <VideoTrack trackRef={screenTracks[0]} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />

            {/* Screen Share Floating Overlay Controls */}
            <Flex position="absolute" top={isScreenShareMaximized ? 4 : 3} right={isScreenShareMaximized ? 4 : 3} gap={2} zIndex={40} dir="rtl">


              <Button
                leftIcon={isScreenShareMaximized ? <MdFullscreenExit /> : <MdFullscreen />}
                size="xs"
                colorScheme={isScreenShareMaximized ? "orange" : "gray"}
                variant="solid"
                onClick={handleFullscreenShare}
                borderRadius="lg"
                boxShadow="lg"
                bg={isScreenShareMaximized ? "orange.500" : "rgba(15, 23, 42, 0.85)"}
                border="1px solid rgba(255,255,255,0.2)"
                backdropFilter="blur(8px)"
                color="white"
              >
                {isScreenShareMaximized ? "تصغير" : "ملء الشاشة"}
              </Button>
            </Flex>

            <Box position="absolute" bottom={isScreenShareMaximized ? 20 : 3} right={3} bg="rgba(0,0,0,0.75)" px={3} py={1} borderRadius="lg" zIndex={40} border="1px solid rgba(255,255,255,0.1)">
              <Text fontSize="xs" color="white" fontWeight="bold">شاشة مشاركة</Text>
            </Box>
          </Box>
        )}

        {/* Regular Participants Grid (Only shown when screen is NOT shared) */}
        {!hasScreenShare && (
          <Box
            flex={1}
            w="100%"
            h="100%"
            overflowY="auto"
            pr={1}
            sx={{ '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.2)', borderRadius: '4px' } }}
          >
            <SimpleGrid columns={gridColumns} spacing={4} h="100%">
              {participants.map((p) => {
                const camTrack = cameraTracks.find(t => t.participant?.identity === p.identity);

                return (
                  <ParticipantCard
                    key={p.identity}
                    participant={p}
                    cameraTrackRef={camTrack}
                    onMute={handleMuteMic}
                    onKick={handleKick}
                    isInstructorOrAdmin={isInstructorOrAdmin}
                  />
                );
              })}
            </SimpleGrid>
          </Box>
        )}
      </Flex>

      {/* 3. FLOATING CARDS CONTROL BAR AT BOTTOM */}
      <Flex
        justify="center"
        w="100%"
        px={{ base: 2, md: 4 }}
        py={0}
        position="absolute"
        bottom={{ base: "env(safe-area-inset-bottom, 8px)", md: "12px" }}
        left={0}
        right={0}
        zIndex={250}
        pointerEvents="none"
      >
        <HStack
          spacing={{ base: 1.5, sm: 2, md: 3 }}
          bg="rgba(15, 23, 42, 0.95)"
          px={{ base: 2, sm: 3, md: 6 }}
          py={{ base: 2, md: 3 }}
          borderRadius="2xl"
          border="1px solid rgba(255,255,255,0.15)"
          boxShadow="0 10px 40px rgba(0, 0, 0, 0.6)"
          backdropFilter="blur(16px)"
          pointerEvents="auto"
          dir="rtl"
          maxW="100%"
          overflowX="auto"
          sx={{ '&::-webkit-scrollbar': { display: 'none' }, 'scrollbarWidth': 'none', 'msOverflowStyle': 'none' }}
        >
          {/* Mic Button with Locked status indicator */}
          <Tooltip
            label={
              !isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted
                ? "الميكروفون مقفل من طرف الأستاذ (اضغط رفع اليد ✋ لطلب الكلمة)"
                : (isMicrophoneEnabled ? "كتم الميكروفون" : "تشغيل الميكروفون")
            }
            hasArrow
          >
            <Box position="relative">
              <IconButton
                icon={
                  !isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted
                    ? <MdMicOff />
                    : (isMicrophoneEnabled ? <MdMic /> : <MdMicOff />)
                }
                onClick={handleToggleMic}
                isDisabled={!isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted}
                cursor={!isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted ? "not-allowed" : "pointer"}
                colorScheme={
                  !isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted
                    ? "gray"
                    : (isMicrophoneEnabled ? "gray" : "red")
                }
                variant="solid"
                borderRadius="xl"
                size={{ base: "md", md: "lg" }}
                aria-label="Toggle Mic"
                opacity={!isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted ? 0.6 : 1}
              />
              {!isInstructorOrAdmin && isMuteAllLocked && !isIndividualMicGranted && (
                <Badge
                  position="absolute"
                  top="-6px"
                  right="-6px"
                  bg="red.500"
                  color="white"
                  borderRadius="full"
                  px={1}
                  py={0.5}
                  fontSize="10px"
                  boxShadow="0 0 6px rgba(239, 68, 68, 0.8)"
                >
                  🔒
                </Badge>
              )}
            </Box>
          </Tooltip>

          {/* Camera Button */}
          <Tooltip label={isCameraEnabled ? "إيقاف الكاميرا" : "تشغيل الكاميرا"} hasArrow>
            <IconButton
              icon={isCameraEnabled ? <MdVideocam /> : <MdVideocamOff />}
              onClick={handleToggleCamera}
              colorScheme={isCameraEnabled ? "gray" : "red"}
              variant="solid"
              borderRadius="xl"
              size={{ base: "md", md: "lg" }}
              aria-label="Toggle Camera"
            />
          </Tooltip>

          {/* Screen Share Button */}
          <Tooltip label={isScreenShareEnabled ? "إيقاف مشاركة الشاشة" : "مشاركة الشاشة"} hasArrow>
            <IconButton
              icon={isScreenShareEnabled ? <MdStopScreenShare /> : <MdScreenShare />}
              onClick={handleToggleScreenShare}
              colorScheme={isScreenShareEnabled ? "green" : "gray"}
              variant="solid"
              borderRadius="xl"
              size={{ base: "md", md: "lg" }}
              aria-label="Toggle Screen Share"
            />
          </Tooltip>

          {/* Student: Raise Hand Button */}
          {!isInstructorOrAdmin && (
            <Tooltip label={hasRaisedHand ? "خفض اليد" : "رفع اليد لطلب الكلمة"} hasArrow>
              <Button
                leftIcon={<Text fontSize={{ base: "sm", md: "md" }}>✋</Text>}
                onClick={handleToggleRaiseHand}
                colorScheme={hasRaisedHand ? "yellow" : "gray"}
                variant="solid"
                borderRadius="xl"
                size={{ base: "md", md: "lg" }}
                px={{ base: 2.5, md: 4 }}
                fontSize={{ base: "xs", md: "sm" }}
                border={hasRaisedHand ? "2px solid #f59e0b" : "1px solid rgba(255,255,255,0.2)"}
                boxShadow={hasRaisedHand ? "0 0 16px rgba(245, 158, 11, 0.6)" : "none"}
                animation={hasRaisedHand ? "pulse 1.8s infinite" : "none"}
                sx={{ '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' } } }}
              >
                {hasRaisedHand ? "خفض اليد" : "رفع اليد"}
              </Button>
            </Tooltip>
          )}

          {/* Instructor: Raised Hands Queue Alert Button */}
          {isInstructorOrAdmin && raisedHands.length > 0 && (
            <Tooltip label="الطلاب الراغبون في التحدث (طلبات رفع اليد)" hasArrow>
              <Button
                leftIcon={<Text fontSize={{ base: "sm", md: "md" }}>✋</Text>}
                colorScheme="yellow"
                variant="solid"
                onClick={() => setShowRaisedHandsModal(true)}
                borderRadius="xl"
                size={{ base: "md", md: "lg" }}
                px={{ base: 2.5, md: 4 }}
                fontSize={{ base: "xs", md: "sm" }}
                animation="pulse 1.8s infinite"
                sx={{ '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.06)' } } }}
              >
                طلبات ({raisedHands.length})
              </Button>
            </Tooltip>
          )}

          {/* Instructor: Mute All & Lock Controls */}
          {isInstructorOrAdmin && (
            <Tooltip label={isMuteAllLocked ? "الميكروفونات مقفلة على الطلاب - اضغط للفتح للجميع" : "كتم الميكروفونات وقفلها على جميع الطلاب"} hasArrow>
              <Button
                leftIcon={isMuteAllLocked ? <MdLockOpen /> : <MdLock />}
                colorScheme={isMuteAllLocked ? "green" : "red"}
                variant={isMuteAllLocked ? "solid" : "subtle"}
                onClick={handleToggleMuteAllLock}
                borderRadius="xl"
                size={{ base: "md", md: "lg" }}
                px={{ base: 2, md: 4 }}
                fontSize={{ base: "10px", md: "xs" }}
                fontWeight="bold"
              >
                {isMuteAllLocked ? "فتح المايك" : "قفل المايك"}
              </Button>
            </Tooltip>
          )}



          {/* Role-based Exit Button */}
          {isInstructorOrAdmin ? (
            /* Teacher / Admin → End session for everyone */
            <Button
              leftIcon={<MdCallEnd />}
              colorScheme="red"
              variant="solid"
              borderRadius="xl"
              size={{ base: "md", md: "lg" }}
              px={{ base: 3, md: 6 }}
              fontWeight="bold"
              onClick={handleEndMeetingForEveryone}
            >
              إنهاء
            </Button>
          ) : (
            /* Student → Leave only (does NOT end the session) */
            <Button
              leftIcon={<MdCallEnd />}
              colorScheme="orange"
              variant="solid"
              borderRadius="xl"
              size={{ base: "md", md: "lg" }}
              px={{ base: 3, md: 6 }}
              fontWeight="bold"
              onClick={() => {
                if (localParticipant) {
                  localParticipant.disconnect();
                }
              }}
            >
              مغادرة
            </Button>
          )}
        </HStack>
      </Flex>

      {/* 4. WAITING ROOM CARD OVERLAY */}
      {isInstructorOrAdmin && pendingRequests.length > 0 && (
        <Box position="absolute" top={16} right={4} zIndex={120} w="320px" pointerEvents="auto">
          <Box bg="white" borderRadius="2xl" shadow="2xl" overflow="hidden" border="1px solid #e2e8f0">
            <Flex bg="#f8fafc" p={3.5} borderBottom="1px solid #e2e8f0" justify="space-between" align="center">
              <Text fontSize="13px" color="#0f172a" fontWeight="bold">
                غرفة الانتظار (Waiting Room)
              </Text>
              <Badge colorScheme="blue" borderRadius="full">{pendingRequests.length}</Badge>
            </Flex>
            <VStack maxH="280px" overflowY="auto" p={0} spacing={0} bg="white">
              {pendingRequests.map((req, idx) => (
                <Flex key={idx} w="full" align="center" justify="space-between" p={3} borderBottom="1px solid #f1f5f9">
                  <HStack spacing={2}>
                    <Avatar size="sm" name={req.user?.username || "طالب"} />
                    <Text fontSize="13px" color="#0f172a" fontWeight="600">{req.user?.username || "طالب"}</Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Button size="xs" colorScheme="blue" borderRadius="lg" px={3} onClick={() => handleAdmit(req, true)}>قبول</Button>
                    <Button size="xs" colorScheme="gray" variant="ghost" borderRadius="lg" onClick={() => handleAdmit(req, false)}>رفض</Button>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Box>
        </Box>
      )}

      {/* 5. RAISED HANDS POP-UP MODAL (For Instructors/Admins) */}
      <Modal isOpen={showRaisedHandsModal} onClose={() => setShowRaisedHandsModal(false)} isCentered size="md">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
        <ModalContent bg="#1e293b" color="white" borderRadius="2xl" border="1px solid rgba(255,255,255,0.15)" dir="rtl" p={1}>
          <ModalHeader borderBottom="1px solid rgba(255,255,255,0.1)" pb={3} display="flex" alignItems="center" justifyContent="space-between">
            <HStack spacing={2}>
              <Text fontSize="md" fontWeight="bold">✋ طلبات رفع اليد للتحدث</Text>
              <Badge colorScheme="yellow" borderRadius="full" px={2.5} py={0.5} fontSize="xs">{raisedHands.length}</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton left="12px" right="auto" color="gray.300" />
          <ModalBody py={4} maxH="400px" overflowY="auto">
            {raisedHands.length === 0 ? (
              <VStack py={8} spacing={3} color="gray.400" textAlign="center">
                <Text fontSize="3xl">✋</Text>
                <Text fontSize="sm">لا توجد طلبات رفع يد حالياً من الطلاب</Text>
              </VStack>
            ) : (
              <VStack spacing={2.5} align="stretch">
                {raisedHands.map((hand, idx) => (
                  <Flex
                    key={idx}
                    align="center"
                    justify="space-between"
                    p={3}
                    borderRadius="xl"
                    bg="rgba(15, 23, 42, 0.85)"
                    border="1px solid rgba(245, 158, 11, 0.35)"
                    boxShadow="0 4px 12px rgba(0,0,0,0.3)"
                  >
                    <HStack spacing={3}>
                      <Avatar size="sm" name={hand.name || "طالب"} bg="orange.500" color="white" />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="bold" color="white">{hand.name || "طالب"}</Text>
                        <Text fontSize="11px" color="yellow.400">يرغب في أخذ الكلمة والتحدث</Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        colorScheme="green"
                        leftIcon={<MdMic />}
                        borderRadius="lg"
                        px={3}
                        onClick={() => handleAllowStudentMic(hand)}
                      >
                        فتح المايك
                      </Button>
                      <IconButton
                        size="xs"
                        colorScheme="gray"
                        variant="ghost"
                        icon={<MdStop />}
                        borderRadius="lg"
                        aria-label="Lower hand"
                        onClick={() => handleDismissHand(hand)}
                        title="إنزال اليد"
                      />
                    </HStack>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid rgba(255,255,255,0.1)" pt={3}>
            <Button size="sm" variant="ghost" colorScheme="gray" onClick={() => setShowRaisedHandsModal(false)} borderRadius="xl">
              إغلاق
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
}

